import React, { useMemo } from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Bubble } from 'tea-component';
import GameImage from '@home/components/GameImage';
import { getName } from '@home/utils/common';
import type { IntelligenceReportDataInfo } from '../../types';
import type { TReportConfigMap } from '@intelligence/utils/reportConfig';
import { resolveReportConfig } from '@intelligence/utils/reportConfig';
import { sanitizeField, isDatasourceReport, getDatasourceDisplay } from '../../utils';
import {
  buildDatasourceAuthor,
  buildReportAuthors,
  getAuthorDisplayName,
} from '@intelligence/components/ReportCard/authors';
import './index.scss';

interface IPopularReportItemProps {
  /** 排名（从 1 开始） */
  rank: number;
  /** 报告数据 */
  report: IntelligenceReportDataInfo;
  reportSourceConfig: TReportConfigMap;
  /** 是否英文环境 */
  isEn: boolean;
  /** 点击回调 */
  onClick?: (report: IntelligenceReportDataInfo) => void;
}

const PopularReportItem: React.FC<IPopularReportItemProps> = ({
  rank,
  report,
  reportSourceConfig,
  isEn,
  onClick,
}) => {
  const title = sanitizeField(getName(isEn, report.title_en, report.title));

  const authors = useMemo(() => {
    if (isDatasourceReport(report.system)) {
      const { display_source: displaySource } = resolveReportConfig(report, reportSourceConfig);
      const sourceConfig = getDatasourceDisplay(displaySource);
      return [buildDatasourceAuthor(sourceConfig?.name || '', sourceConfig?.img || '')];
    }
    return buildReportAuthors(report, { textOrder: 'en-cn' });
  }, [report, isEn, reportSourceConfig]);
  const authorLang = isEn ? 0 : 1;
  const authorDisplayName = authors
    .map((author) => getAuthorDisplayName(author, authorLang))
    .filter(Boolean)
    .join('，');

  const time = report.create_time
    ? moment(report.create_time).format('YYYY-MM-DD')
    : '';
  const cover = report.cover || '';
  const isTop = rank >= 1 && rank <= 3;

  const bubbleContent = useMemo((): React.ReactNode => {
    const hasMetaInfo = !!(time || authorDisplayName);

    return (
      <div className="dbi-popular-report-item__bubble">
        <div className="dbi-popular-report-item__bubble-title">{title}</div>
        {hasMetaInfo && (
          <div className="dbi-popular-report-item__bubble-meta">
            {time && (
              <span className="dbi-popular-report-item__bubble-meta-time">
                {time}
              </span>
            )}
            {time && authorDisplayName && (
              <span className="dbi-popular-report-item__bubble-meta-sep" />
            )}
            {authorDisplayName && (
              <span className="dbi-popular-report-item__bubble-meta-author">
                {authorDisplayName}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }, [title, time, authorDisplayName]);

  const renderRank = (): React.ReactNode => {
    if (isTop) {
      return (
        <span
          className={`dbi-popular-report-item__rank-icon dbi-popular-report-item__rank-icon--${rank}`}
        />
      );
    }
    return <span className="dbi-popular-report-item__rank-num">{rank}</span>;
  };

  return (
    <Bubble content={bubbleContent}>
      <div
        className={classNames('dbi-popular-report-item', {
          'dbi-popular-report-item--top': isTop,
        })}
        onClick={() => onClick?.(report)}
      >
        <div className="dbi-popular-report-item__rank">{renderRank()}</div>
        <GameImage
          className="dbi-popular-report-item__cover"
          src={cover === 'error' ? '' : cover}
          alt={title}
          entityType="report"
          loading="lazy"
        />
        <div className="dbi-popular-report-item__content">
          <div className="dbi-popular-report-item__title">{title}</div>
          <div className="dbi-popular-report-item__meta">
            {time && (
              <span className="dbi-popular-report-item__meta-time">{time}</span>
            )}
            {authorDisplayName && (
              <>
                <span className="dbi-popular-report-item__meta-sep" />
                <span className="dbi-popular-report-item__meta-author">
                  {authorDisplayName}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </Bubble>
  );
};

export default React.memo(PopularReportItem);
